import { useState, useRef, useEffect } from 'react';
import { Draggable, DroppableProvided } from 'react-beautiful-dnd';

import { AddCardForm } from './AddCardForm';
import { KanbanCard } from './KanbanCard';
import { StrictModeDroppable } from './StrictModeDroppable';

import { KanbanColumn as Column , useKanbanStore } from '@/apps/Kanban/stores/useKanbanStore';
import handleException from '@/common/utils/errors';

interface KanbanColumnProps {
  column: Column;
  index: number;
  activeColumnId: string | null;
  onDeleteColumn: (id: string) => void;
  onDeleteCard: (id: string) => void;
  onAddCard: (columnId: string, content: string) => void;
  setActiveColumnId: (id: string | null) => void;
  onCardClick: (card: { id: string; content: string; description: string }) => void;
}

export const KanbanColumn = ({
  column,
  index,
  activeColumnId,
  onDeleteColumn,
  onDeleteCard,
  onAddCard,
  setActiveColumnId,
  onCardClick
}: KanbanColumnProps) => {
  const updateColumn = useKanbanStore(state => state.updateColumn);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(column.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    if (title.trim() && title !== column.title) {
      try {
        await updateColumn(column.id, title.trim());
      } catch (error) {
        handleException(error);
        // Reset to original title if update fails
        setTitle(column.title);
      }
    } else {
      setTitle(column.title);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setTitle(column.title);
      setIsEditing(false);
    }
  };

  return (
    <Draggable draggableId={column.id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="flex-shrink-0 w-80 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col max-h-full"
          style={{
            ...provided.draggableProps.style,
            left: 'auto !important',
            top: 'auto !important',
          }}
        >
          <div
            {...provided.dragHandleProps}
            className="flex-none p-4 flex justify-between items-center"
            style={{ cursor: isEditing ? 'text' : 'grab' }}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                className="font-medium text-gray-700 px-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#FFD66B] focus:border-transparent"
                value={title}
                onBlur={handleBlur}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            ) : (
              <h3
                className="font-medium text-gray-700 px-1 cursor-text"
                onDoubleClick={handleDoubleClick}
              >
                {column.title}
              </h3>
            )}
            <button
              className="text-gray-400 hover:text-red-500 transition-colors"
              onClick={() => onDeleteColumn(column.id)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
              </svg>
            </button>
          </div>

          <StrictModeDroppable droppableId={column.id} type="CARD">
            {(provided: DroppableProvided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex-1 px-4 pb-4 space-y-2 overflow-y-auto"
              >
                {column.cards.map((card, cardIndex) => (
                  <KanbanCard
                    key={card.id}
                    content={card.content}
                    description={card.description}
                    id={card.id}
                    index={cardIndex}
                    onClick={onCardClick}
                    onDelete={onDeleteCard}
                  />
                ))}
                {provided.placeholder}
                <AddCardForm
                  columnId={column.id}
                  isActive={activeColumnId === column.id}
                  onActivate={() => setActiveColumnId(column.id)}
                  onAdd={onAddCard}
                  onCancel={() => setActiveColumnId(null)}
                />
              </div>
            )}
          </StrictModeDroppable>
        </div>
      )}
    </Draggable>
  );
};
